import React from 'react';
import { Card, CardHeader, Divider, CardContent, Grid, Box, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { FranchiseSelectorAdapter } from '../FranshiseSelector';
import { StoreSelector } from '../StoreSelector';
import { BulkUpdateModeSelector, BulkUploadFlow } from '../components';
import { BulkFlowStep } from '../components/BulkFlowIndicator';
import { FranchiseGroup, Store } from '../../../types';
import { useFeatureFlag } from '../../../services/optimizely/optimizelyHooks';
import { FeatureFlags } from '../../../constants/FeatureFlags';
import { BulkUpdateAction } from '../bulkUpdateTypes';

export type BulkUpdateMode = 'manual' | 'bulk-upload';

interface Step1SelectStoresProps {
  selectedFranchises: FranchiseGroup[];
  selectedStores: Store[];
  handleFranchiseChange: (franchises: FranchiseGroup | FranchiseGroup[] | null, stores: Store[]) => void;
  handleStoreSelection: (stores: Store | Store[] | null) => void;
  bulkUpdateMode: BulkUpdateMode;
  onBulkUpdateModeChange: (mode: BulkUpdateMode) => void;
  onBulkFlowStateChange?: (currentStep: BulkFlowStep) => void;
  selectedAction: BulkUpdateAction;
  handleActionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile: boolean;
}

const Step1SelectStores: React.FC<Step1SelectStoresProps> = ({
  selectedFranchises,
  selectedStores,
  handleFranchiseChange,
  handleStoreSelection,
  bulkUpdateMode,
  onBulkUpdateModeChange,
  onBulkFlowStateChange,
  selectedAction,
  handleActionChange,
  isMobile,
}) => {
  const { isEnabled: dealLoversEnabled } = useFeatureFlag(FeatureFlags.DEAL_LOVERS_ENABLED);
  // Gated behind patch editing as well as its own flag: the reconcile stages the same pending operations
  // inline editing does, so offering it while that is off would be a dead end.
  const { isEnabled: patchEditEnabled } = useFeatureFlag(FeatureFlags.PATCH_EDIT_ENABLED);
  const { isEnabled: exclusionFlagEnabled } = useFeatureFlag(FeatureFlags.EXCLUSION_WORKFLOW_ENABLED);
  const exclusionWorkflowEnabled = patchEditEnabled && exclusionFlagEnabled;

  return (
    <>
      <BulkUpdateModeSelector
        mode={bulkUpdateMode}
        onModeChange={onBulkUpdateModeChange}
      />

      {bulkUpdateMode === 'manual' ? (
        <Card data-testid='manualStoreSelectCard'>
          <CardHeader title='Select Stores & Action' data-testid='storesActionHeader'/>
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FranchiseSelectorAdapter
                  label='Select Franchise Groups'
                  testId='franchiseSelector'
                  placeholder='Search for franchise groups...'
                  multiple
                  value={selectedFranchises}
                  onChange={handleFranchiseChange}
                />
                <StoreSelector
                  label='Store Selection'
                  testId='storeSelector'
                  placeholder='Search for stores...'
                  multiple={true}
                  value={selectedStores}
                  onChange={handleStoreSelection}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <FormControl component='fieldset'>
                    <RadioGroup row value={selectedAction} onChange={handleActionChange}>
                      <FormControlLabel
                        value='review'
                        control={<Radio size={isMobile ? 'medium' : 'small'} />}
                        label='Review Menus'
                        data-testid='reviewMenusRadio'
                      />
                      <FormControlLabel
                        value='update'
                        control={<Radio size={isMobile ? 'medium' : 'small'} />}
                        label='Update Menus'
                        data-testid='updateMenusRadio'
                      />
                      {dealLoversEnabled && (
                        <FormControlLabel
                          value='deal-lovers'
                          control={<Radio size={isMobile ? 'medium' : 'small'} />}
                          label='Deal Lovers'
                          data-testid='dealLoversRadio'
                        />
                      )}
                      {exclusionWorkflowEnabled && (
                        <FormControlLabel
                          value='exclusions'
                          control={<Radio size={isMobile ? 'medium' : 'small'} />}
                          label='Exclusions'
                          data-testid='exclusionsRadio'
                        />
                      )}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <BulkUploadFlow onFlowStateChange={onBulkFlowStateChange} />
      )}
    </>
  );
};

export default Step1SelectStores; 